import { Paperclip, X } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { QuestionProps } from "../types";
import { useUploadContext } from "../uploadContext";
import { QuestionShell } from "./QuestionShell";
import { UPLOAD_ACCEPT, UploadError } from "@/features/files/upload";

export function FileUpload(props: QuestionProps) {
  const { t } = useTranslation();
  const id = useId();
  const ctx = useUploadContext();
  const { value, onChange, disabled } = props;
  const ids = Array.isArray(value) ? value : [];
  const [names, setNames] = useState<Record<string, string>>({});
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File | undefined) => {
    if (!file || !ctx) return;
    setError(null);
    setProgress(0);
    try {
      const out = await ctx.upload(file, setProgress);
      setNames((n) => ({ ...n, [out.id]: out.title }));
      onChange([...ids, out.id]);
    } catch (e) {
      setError(e instanceof UploadError ? e.message : t("questionnaire.upload.failed"));
    } finally {
      setProgress(null);
    }
  };

  const nameOf = (fid: string, i: number) => names[fid] ?? ctx?.fileName(fid) ?? `#${i + 1}`;

  return (
    <QuestionShell props={props} inputId={id} error={error}>
      {({ helpId, errorId }) => (
        <div className="flex flex-col gap-3">
          {ids.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {ids.map((fid, i) => (
                <li key={fid} className="flex items-center gap-2 rounded-md bg-surface-muted px-3 py-2 text-sm">
                  <Paperclip aria-hidden="true" className="h-4 w-4 text-fg-muted" />
                  <span className="flex-1 truncate">{nameOf(fid, i)}</span>
                  {!disabled ? (
                    <button
                      type="button"
                      className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-surface"
                      aria-label={t("questionnaire.upload.remove", { name: nameOf(fid, i) })}
                      onClick={() => onChange(ids.filter((x) => x !== fid))}
                    >
                      <X aria-hidden="true" className="h-4 w-4" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          <input
            id={id}
            type="file"
            accept={UPLOAD_ACCEPT}
            disabled={disabled || !ctx || progress !== null}
            aria-describedby={[helpId, errorId].filter(Boolean).join(" ") || undefined}
            className="block w-full text-sm text-fg-muted file:mr-4 file:min-h-11 file:cursor-pointer file:rounded-full file:border-2 file:border-secondary file:bg-surface file:px-5 file:font-semibold file:text-heading hover:file:bg-secondary-subtle disabled:opacity-60"
            onChange={(e) => {
              void onFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          {progress !== null ? (
            <p role="status" className="text-sm text-fg-muted">
              {t("questionnaire.upload.uploading", { pct: progress })}
            </p>
          ) : null}
        </div>
      )}
    </QuestionShell>
  );
}
